import type { PropertySchema } from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';
import type { Widget } from '../content/components/widgets/index.js';
import type {
  AssetArrayProperty,
  AssetProperty,
  BooleanArrayProperty,
  BooleanCheckboxesWidget,
  BooleanProperty,
  CheckboxesWidget,
  CheckboxWidget,
  DatetimeWidget,
  DateWidget,
  DropdownWidget,
  NumberArrayProperty,
  NumberCheckboxesWidget,
  NumberProperty,
  NumberWidget,
  ObjectArrayProperty,
  ObjectProperty,
  RadioButtonsWidget,
  ShapeArrayWidget,
  ShapeEditorProperty,
  ShapeEditorWidget,
  ShapeObjectWidget,
  SingleLineTextWidget,
  StringArrayProperty,
  StringProperty,
  VectorProperty
} from './shape-editor-form-schema';
import type { GeneratedProperty } from './shape-editor-form-schema/properties/property-types/generated.js';

const singleLineTextWidget: SingleLineTextWidget = { name: 'singleLineText' };
const dateWidget: DateWidget = { name: 'date', format: 'date-time' };
const datetimeWidget: DatetimeWidget = {
  name: 'datetime',
  format: 'date-time'
};
const numberWidget: NumberWidget = { name: 'number' };
const checkboxWidget: CheckboxWidget = { name: 'checkbox' };
const objectWidget: ShapeObjectWidget = { name: 'shapeObject' };

const defaultStringOptions = [
  { label: 'Option 1', value: 'option1' },
  { label: 'Option 2', value: 'option2' }
];

const dropdownWidget: DropdownWidget = {
  name: 'dropdown',
  options: defaultStringOptions
};

const radioButtonsWidget: RadioButtonsWidget = {
  name: 'radio',
  options: defaultStringOptions
};

const checkboxesWidget: CheckboxesWidget = {
  name: 'checkboxes',

  options: defaultStringOptions
};

const numberCheckboxesWidget: NumberCheckboxesWidget = {
  name: 'numberCheckboxes',

  options: [
    { label: 'Option 1', value: 1 },
    { label: 'Option 2', value: 2 }
  ]
};

const booleanCheckboxesWidget: BooleanCheckboxesWidget = {
  name: 'booleanCheckboxes',
  options: [
    { label: 'Option 1', value: true },
    { label: 'Option 2', value: false }
  ]
};

const shapeArrayWidget: ShapeArrayWidget = { name: 'shapeArray' };

export const defaultWidgets: Record<string, ShapeEditorWidget> = {
  string: singleLineTextWidget,
  integer: numberWidget,
  number: numberWidget,
  boolean: checkboxWidget,
  object: objectWidget
};

export const defaultArrayWidgets: Record<string, ShapeEditorWidget> = {
  string: checkboxesWidget,
  number: numberCheckboxesWidget,
  boolean: booleanCheckboxesWidget,
  object: shapeArrayWidget
};

const defaultPropertyValues = {
  key: '',
  name: '',
  title: '',
  isRequired: false,
  isArray: false as const,
  isAsset: false as const
};
const stringProperty: StringProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'string',
  type: 'string',
  widget: singleLineTextWidget
};

const stringArrayProperty: StringArrayProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'stringArray',
  type: 'string',
  isArray: true,
  widget: checkboxesWidget
};

const numberProperty: NumberProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'number',
  type: 'number',
  widget: numberWidget
};

const numberArrayProperty: NumberArrayProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'numberArray',
  type: 'number',
  isArray: true,
  widget: numberCheckboxesWidget
};

const booleanProperty: BooleanProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'boolean',
  type: 'boolean',
  widget: checkboxWidget
};

const booleanArrayProperty: BooleanArrayProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'booleanArray',
  type: 'boolean',
  widget: booleanCheckboxesWidget,
  isArray: true
};

const objectProperty: ObjectProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'object',
  type: 'object',
  behavior: {
    type: 'inline',
    refs: []
  },
  widget: objectWidget
};

const objectArrayProperty: ObjectArrayProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'objectArray',
  type: 'object',
  behavior: {
    type: 'inline',
    refs: []
  },
  isArray: true,
  widget: shapeArrayWidget
};

const assetProperty: AssetProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'asset',
  type: 'object',
  behavior: {
    type: 'relationship',
    refs: ['Asset'],
    backreferenceEnabled: false
  },
  widget: {
    name: 'image'
  },
  isAsset: true
};

const assetArrayProperty: AssetArrayProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'assetArray',
  type: 'object',
  behavior: {
    type: 'relationship',
    refs: ['Asset'],
    backreferenceEnabled: false
  },
  widget: {
    name: 'image'
  },
  isArray: true,
  isAsset: true
};

const vectorProperty: VectorProperty = {
  ...defaultPropertyValues,
  content: [],
  shapePropertyType: 'vector',
  type: 'number',
  isArray: true
};

const generatedProperty: GeneratedProperty = {
  ...defaultPropertyValues,
  shapePropertyType: 'generated',
  type: 'string',
  content: []
};

export const propertyDefaults: Record<string, ShapeEditorProperty> = {
  string: stringProperty,
  stringArray: stringArrayProperty,
  number: numberProperty,
  numberArray: numberArrayProperty,
  boolean: booleanProperty,
  booleanArray: booleanArrayProperty,
  object: objectProperty,
  objectArray: objectArrayProperty,
  asset: assetProperty,
  assetArray: assetArrayProperty,
  vector: vectorProperty,
  generated: generatedProperty
};

function capitalizeKeys(toAdd: Record<string, unknown>) {
  const result: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(toAdd)) {
    const formattedName = `${name[0].toUpperCase()}${name.substring(1)}`;
    result[formattedName] = value;
  }

  return result;
}

export const definitionDefaults = capitalizeKeys({
  // properties
  stringProperty,
  stringArrayProperty,
  numberProperty,
  numberArrayProperty,
  booleanProperty,
  booleanArrayProperty,
  objectProperty,
  objectArrayProperty,
  assetProperty,
  assetArrayProperty,
  // widgets
  singleLineTextWidget,
  dateWidget,
  datetimeWidget,
  radioButtonsWidget,
  dropdownWidget,
  numberWidget,
  checkboxWidget,
  objectWidget,
  checkboxesWidget,
  numberCheckboxesWidget,
  booleanCheckboxesWidget,
  shapeArrayWidget
});

export function getDefaultWidget(schema: PropertySchema): Maybe<Widget> {
  return getDefaultShapeEditorWidget(schema)?.name;
}

function normalizeType(type: string): string {
  return type === 'integer' ? 'number' : type;
}

export function getDefaultShapeEditorWidget(schema: PropertySchema): Maybe<ShapeEditorWidget> {
  const { type, items } = schema;

  if (type === 'array') {
    if (items?.type && typeof items.type === 'string') {
      return defaultArrayWidgets[normalizeType(items.type)];
    }

    return defaultArrayWidgets.object;
  }

  if (typeof type === 'string') {
    return defaultWidgets[normalizeType(type)];
  }
}
